// The shared transaction-detail body used by both the phone full-height
// sheet and the tablet/desktop side panel (`TransactionDetailPage`) --
// execution plan §7.3: "share one body component" so semantic order and
// available actions never diverge across bands.
//
// User correction (M2 execution plan §S9, spec §4.2, superseded by §14): a
// single-line-item transaction's type tag opens the compact `TypeChooser`
// popover (D-C); the actual write is owned by the SHARED
// `useCategoryCorrection` hook, not a local copy of the PATCH/
// apply-to-similar logic. A `409 changed_since` response is never silently
// retried -- it renders the honest "changed elsewhere, reload" state
// (design contract §8.2). Splits (`txn.line_items.length > 1`) keep no
// correction affordance here -- `SplitEditor` already states editing
// splits is not available yet, and there is no per-split write endpoint to
// wire.
//
// Founder revision §14 (2026-09-08, supersedes the earlier "scope tag
// always flips instantly" / bottom-right tray design): the scope tag opens
// a small anchored menu (`CategoryChip`'s `onSelectScope`) -- radio
// Personal/Business + an "Apply to all items from {merchant}" checkbox,
// through the same `handleSelectCategory` path used by the type chooser.
// There is no separate "Change category" button and no scope-filtered
// picker -- the type tag's `TypeChooser` popover is the one place to
// change the leaf, and both it and the scope menu land on the same shared
// correction state; Undo renders inline next to the changed control.
//
// Merged with main (frontend_cont/e2e_wire): `txn` is the canonical
// `PrimaryTransactionDetail` the detail route now resolves (return-context/
// alias/tombstone handling lives in `TransactionDetailPage`, not here).
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@moneyskyline/ui";
import { AmountText } from "@/components/AmountText";
import { CategoryChip } from "@/components/CategoryChip";
import { MerchantName } from "@/components/MerchantName";
import { TypeBadge } from "@/components/TypeBadge";
import { formatMoney, useMoneyFormat } from "@/lib/money";
import { formatHumanDate } from "@/lib/date";
import type { PrimaryTransactionDetail } from "@/api/hooks/useTransaction";
import { useCategoriesMap } from "@/api/hooks/useCategories";
import { counterpartScope, findScopeCounterpart, flipTarget, scopeOfCategory, scopeOfSlug } from "./scopeFlip";
import { TypeChooser, typeChooserContentId } from "./TypeChooser";
import { useCategoryCorrection } from "./useCategoryCorrection";
import { AmendmentTrail } from "./AmendmentTrail";
import { ReceiptSlot } from "./ReceiptSlot";
import { SimilarList } from "./SimilarList";
import { publicAllocationDetails, HistoryList } from "./HistoryList";
import { useShellContext } from "@/app/shell/AppShell";
import { viewportBandFromShellBand, type TelemetryContext } from "./telemetry";
import { SplitEditor } from "./SplitEditor";

export function TransactionDetailPanel({ txn }: { txn: PrimaryTransactionDetail }) {
  const { masked } = useMoneyFormat();
  const merchantDisplay = txn.merchant?.display_name ?? txn.merchant_raw;
  const isAmended = txn.revision > 1 && txn.amended_from !== null;
  const removed = txn.superseded_at !== null;
  const primaryLineItem = txn.line_items[0];
  const isSplitTxn = txn.line_items.length > 1;
  const substituted =
    txn.merchant !== null && txn.merchant.display_name !== txn.merchant_raw;

  const [typeChooserOpen, setTypeChooserOpen] = useState(false);
  // §4.3: anchor the chooser to the real type button (CategoryChip's
  // forwarded ref), not the enclosing group.
  const typeButtonRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();
  const categoriesQuery = useCategoriesMap();
  const correction = useCategoryCorrection();
  // ENG-R1-001: separate checkbox state per surface (scope menu vs
  // TypeChooser footer) -- see FeedRow's identical fix/comment. A single
  // shared state let a box ticked in one surface leak into the other's next
  // write after a dismissal (Escape/outside click) with no write in between.
  const [scopeApplyToMerchant, setScopeApplyToMerchant] = useState(false);
  const [chooserApplyToMerchant, setChooserApplyToMerchant] = useState(false);
  const { band } = useShellContext();
  const telemetryContext: TelemetryContext = {
    surface: "detail",
    viewport_band: viewportBandFromShellBand(band),
    input_method: "unknown",
  };

  // ENG-R1-002: every single-line-item, non-removed, non-split transaction
  // must be able to open the chooser -- including an uncategorized item
  // (no `category_slug` at all).
  const canCorrect = txn.line_items.length === 1 && !removed;
  const currentCategory = primaryLineItem?.category_id
    ? categoriesQuery.byId.get(primaryLineItem.category_id)
    : undefined;
  // ENG-R1-001: scope is derived from the category ROW first (a custom
  // leaf's slug carries no scope prefix), falling back to the slug prefix
  // only when the row itself can't be found (unknown/stale category_id).
  // A definite `is_business: true` on the LINE ITEM (not derivable from the
  // category row/slug, e.g. an account-level business override on a legacy
  // dot-path category) is a real, known fact -- surface it rather than
  // silently downgrading to "Not set" (spec's Uncategorized row: "do not
  // infer Personal from a false business flag" implies `is_business: true`
  // IS a legitimate business signal; only `false` must not be used to infer
  // Personal, since it could also mean transfer/unresolved).
  const resolvedScope =
    scopeOfCategory(currentCategory) ??
    scopeOfSlug(primaryLineItem?.category_slug) ??
    (primaryLineItem?.is_business ? "business" : null);
  const resolvedKind: "expense" | "income" | null =
    currentCategory?.kind === "expense" || currentCategory?.kind === "income"
      ? currentCategory.kind
      : null;
  // DES-R1-001/002: transfer-scoped or kind-unresolved items (including
  // uncategorized) get the scope-agnostic TypeChooser mode instead of
  // losing the affordance entirely.
  const isScopedChooser =
    (resolvedScope === "personal" || resolvedScope === "business") && resolvedKind !== null;
  const typeLabel =
    !primaryLineItem?.category_id
      ? "Uncategorized"
      : categoriesQuery.data === undefined
        ? undefined
        : (currentCategory?.name ??
          // ENG-R3-001: loaded but no row for this id (e.g. taxonomy
          // version bump) -- say so; never fall back to the slug.
          "Unknown category");
  // §2 detail order: "Show the parent label underneath ('Business expense',
  // for example), not another scope badge."
  const parentLabel = currentCategory?.parent_id
    ? (categoriesQuery.byId.get(currentCategory.parent_id)?.name ?? null)
    : null;

  function handleSelectCategory(
    categoryId: string,
    applyToMerchantValue: boolean,
    telemetry?: import("./useCategoryCorrection").ApplyCategoryTelemetry,
  ) {
    if (!primaryLineItem) return;
    // Belt and braces: pass `is_business` explicitly from the target
    // category where it's cheaply known here, even though the backend now
    // derives it server-side when omitted (ENG-R1-001/Q2).
    const targetCategory = categoriesQuery.byId.get(categoryId);
    correction.applyCategory({
      lineItemId: primaryLineItem.line_item_id,
      version: primaryLineItem.version,
      categoryId,
      isBusiness: targetCategory?.is_business,
      categoryLabel: targetCategory?.name ?? "this category",
      merchant: merchantDisplay,
      previousCategoryId: primaryLineItem.category_id,
      previousIsBusiness: primaryLineItem.is_business,
      transactionId: txn.transaction_id,
      applyToMerchant: applyToMerchantValue,
      telemetry,
    });
  }

  function handleTypeChooserSelect(categoryId: string, selection: import("./TypeChooser").TypeChooserSelection) {
    if (!primaryLineItem) return;
    // ENG-R1-001: the chooser's own checkbox -- consumed/reset here, never
    // the scope menu's.
    const consumeApplyToMerchant = chooserApplyToMerchant;
    setChooserApplyToMerchant(false);
    const chosenCategory = categoriesQuery.byId.get(categoryId);
    const toScope = scopeOfCategory(chosenCategory);
    if (toScope !== "personal" && toScope !== "business") {
      handleSelectCategory(categoryId, consumeApplyToMerchant);
      return;
    }
    handleSelectCategory(categoryId, consumeApplyToMerchant, {
      kind: "type_change",
      context: telemetryContext,
      fromTypeId: primaryLineItem.category_id,
      toTypeId: categoryId,
      fromScope: resolvedScope === "transfer" ? null : resolvedScope,
      toScope,
      selection,
    });
  }

  function handleSelectScope() {
    if (!primaryLineItem?.category_slug || !categoriesQuery.data || resolvedScope === "transfer" || resolvedScope === null) return;
    const target = flipTarget(primaryLineItem.category_slug, categoriesQuery.data.categories);
    if (!target) return;
    const counterpart = findScopeCounterpart(primaryLineItem.category_slug, categoriesQuery.data.categories);
    // ENG-R1-001: the scope menu's own checkbox.
    const consumeApplyToMerchant = scopeApplyToMerchant;
    setScopeApplyToMerchant(false);
    handleSelectCategory(target.category_id, consumeApplyToMerchant, {
      kind: "scope_flip",
      context: telemetryContext,
      fromScope: resolvedScope,
      toScope: counterpartScope(resolvedScope),
      fromTypeId: primaryLineItem.category_id,
      toTypeId: target.category_id,
      mapping: counterpart ? "counterpart" : "catch_all",
    });
  }

  function handleCreateCategory(parentId: string, name: string) {
    if (!primaryLineItem || !categoriesQuery.data) return;
    const parentCategory = categoriesQuery.byId.get(parentId);
    const newScope = scopeOfCategory(parentCategory);
    if (newScope !== "personal" && newScope !== "business") return;
    // ENG-R1-001: the chooser's own checkbox ("Add new type" also lives in
    // the TypeChooser footer).
    const consumeApplyToMerchant = chooserApplyToMerchant;
    setChooserApplyToMerchant(false);
    correction.createAndApply({
      parentId,
      name,
      lineItemId: primaryLineItem.line_item_id,
      version: primaryLineItem.version,
      merchant: merchantDisplay,
      previousCategoryId: primaryLineItem.category_id,
      previousIsBusiness: primaryLineItem.is_business,
      transactionId: txn.transaction_id,
      applyToMerchant: consumeApplyToMerchant,
      telemetry: {
        context: telemetryContext,
        fromTypeId: primaryLineItem.category_id,
        fromScope: resolvedScope === "transfer" ? null : resolvedScope,
        toScope: newScope,
      },
      // ENG-R1-006: close the popover once the category ITSELF is
      // confirmed created, independent of whether the follow-up apply
      // succeeds -- a `created_apply_failed` banner (below) covers that.
      onCreated: () => setTypeChooserOpen(false),
    });
  }

  function handleReload() {
    queryClient.invalidateQueries({
      queryKey: ["transactions", "detail", txn.transaction_id],
    });
    correction.dismiss();
  }

  return (
    <div data-testid="transaction-detail-panel" className="min-w-0 max-w-2xl">
      <h2 className="break-words text-lg font-semibold">
        <MerchantName
          display={merchantDisplay}
          raw={txn.merchant_raw}
          substituted={substituted}
          className="break-words"
        />
      </h2>
      <AmountText
        value={txn.amount}
        currency={txn.currency}
        struck={removed}
        variant={txn.has_live_link ? "transfer" : "amount"}
        className="text-figure-sm"
      />
      {/* §14 decision 4: one Pending indicator per view -- the badge below,
          never a second one from AmountText. */}

      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>
          {txn.account.display_name} •{txn.account.last4}
        </span>
        <span aria-hidden="true">·</span>
        <time dateTime={txn.posted_at} aria-label={`Posted ${txn.posted_at}`}>
          {formatHumanDate(txn.posted_at)}
        </time>
        <span aria-hidden="true">·</span>
        {txn.pending && (
          <TypeBadge
            kind="pending"
            tone={
              txn.has_live_link
                ? undefined
                : Number(txn.amount) < 0
                  ? "expense"
                  : Number(txn.amount) > 0
                    ? "income"
                    : undefined
            }
          />
        )}
        {!txn.pending && <span>posted</span>}
        {txn.has_live_link && <TypeBadge kind="transfer" />}
        {isAmended && (
          <TypeBadge
            kind="amended"
            detail={
              masked
                ? undefined
                : `${formatMoney(txn.amended_from, { currency: txn.currency })} → ${formatMoney(txn.amount, { currency: txn.currency })}`
            }
          />
        )}
        {/* No `detail` on the removed badge: `removal_reason` is a provider
            payload term, not a user-facing explanation (AGENTS.md Data & UI
            honesty 4) -- the panel's private-DOM test asserts it never
            appears. */}
        {removed && <TypeBadge kind="removed" />}
      </div>

      {/* One labeled Category section (spec §2/§3) -- the parent label
          ("Business expense") sits under the group instead of a second,
          duplicate scope badge; single-allocation transactions no longer
          repeat the category name in a separate line below (see the
          removed duplicate near the split/single-allocation block). */}
      <div className="mt-3 border-t border-border pt-2.5" data-testid="detail-category-section">
        <h3 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Category
        </h3>
        {isSplitTxn ? (
          <CategoryChip
            size="detail"
            path={["Split"]}
            source={primaryLineItem?.source ?? "ai"}
            split={txn.line_items.length}
            merchantName={merchantDisplay}
          />
        ) : removed ? (
          <CategoryChip
            size="detail"
            path={primaryLineItem?.category_slug ? primaryLineItem.category_slug.split(".") : ["Removed"]}
            source={primaryLineItem?.source ?? "ai"}
            removed
            typeLabel={typeLabel}
            merchantName={merchantDisplay}
          />
        ) : canCorrect && categoriesQuery.data ? (
          <>
            <CategoryChip
            size="detail"
              ref={typeButtonRef}
              path={primaryLineItem.category_slug ? primaryLineItem.category_slug.split(".") : ["Uncategorized"]}
              source={primaryLineItem.source}
              slug={primaryLineItem.category_slug}
              // §3 "Saving correction": paint the destination pair
              // immediately -- see FeedRow's identical comment.
              scope={correction.state.kind === "saving" ? correction.state.target.scope : resolvedScope}
              typeLabel={correction.state.kind === "saving" ? correction.state.target.categoryLabel : typeLabel}
              parentLabel={parentLabel}
              merchantName={merchantDisplay}
              onSelectScope={handleSelectScope}
              applyToMerchantChecked={scopeApplyToMerchant}
              onApplyToMerchantChange={setScopeApplyToMerchant}
              onScopeMenuOpenChange={(open) => {
                if (!open) setScopeApplyToMerchant(false);
              }}
              merchantApplyCount={correction.lastKnownCounts?.allTime ?? null}
              onOpenTypeChooser={() => setTypeChooserOpen(true)}
              saving={correction.state.kind === "saving"}
              busy={correction.saving}
              typeChooserOpen={typeChooserOpen}
              typeChooserId={typeChooserContentId(primaryLineItem.line_item_id)}
            />
            <TypeChooser
              anchorRef={typeButtonRef}
              open={typeChooserOpen}
              onOpenChange={(open) => {
                setTypeChooserOpen(open);
                // ENG-R1-001: reset THIS surface's checkbox on every close.
                if (!open) setChooserApplyToMerchant(false);
              }}
              lineItemId={primaryLineItem.line_item_id}
              scope={isScopedChooser ? (resolvedScope as "personal" | "business") : "any"}
              kind={isScopedChooser ? (resolvedKind as "expense" | "income") : undefined}
              categories={categoriesQuery.data.categories}
              currentCategoryId={primaryLineItem.category_id}
              currentParentId={currentCategory?.parent_id ?? null}
              merchantName={merchantDisplay}
              applyToMerchantChecked={chooserApplyToMerchant}
              onApplyToMerchantChange={setChooserApplyToMerchant}
              merchantApplyCount={correction.lastKnownCounts?.allTime ?? null}
              onSelect={handleTypeChooserSelect}
              onCreateCategory={handleCreateCategory}
              busy={correction.saving}
              creating={correction.state.kind === "creating"}
              createError={
                correction.state.kind === "create_failed"
                  ? "Couldn't add this type. Try again."
                  : null
              }
              surface="detail"
              entry={
                !primaryLineItem.category_id
                  ? "uncategorized"
                  : resolvedScope === "transfer"
                    ? "transfer"
                    : isScopedChooser
                      ? "categorized"
                      : "unknown"
              }
            />
          </>
        ) : primaryLineItem?.category_slug ? (
          <CategoryChip
            size="detail"
            path={primaryLineItem.category_slug.split(".")}
            source={primaryLineItem.source}
            slug={primaryLineItem.category_slug}
            scope={resolvedScope}
            // The type tag must show the category NAME, never the raw
            // slug (ENG-R1-004/DES-R1-001) -- resolved by category_id
            // from `useCategories`, with a neutral placeholder while
            // that query is still loading.
            typeLabel={typeLabel}
            typeLabelPending={categoriesQuery.data === undefined}
            parentLabel={parentLabel}
            merchantName={merchantDisplay}
          />
        ) : (
          !txn.has_live_link && <span className="text-sm text-muted-foreground">Uncategorized</span>
        )}
      </div>

      {/* No M1 event source yet (events ship in M2), so EventChip is not
          rendered here -- it stays exercised only by its own colocated test
          (execution plan §9.9: nothing invented ahead of the real endpoint). */}

      {txn.line_items.length > 1 && !removed && (
        <SplitEditor
          totalAmount={txn.amount}
          currency={txn.currency}
          lineItems={txn.line_items}
        />
      )}
      {/* Single-allocation transactions no longer repeat the category name
          in a second line here (spec §2 "no duplicate allocation line for
          single allocation") -- the labeled Category section above and the
          top-of-panel AmountText already cover both facts. */}

      {canCorrect && correction.state.kind === "changed_since" && (
        <div
          className="mt-2.5 rounded-lg border border-degraded-border bg-degraded-surface p-2.5 text-sm"
          role="alert"
          data-testid="change-category-changed-since"
        >
          <p>
            This item changed elsewhere. Reload to see the latest before
            correcting it.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1.5"
            onClick={handleReload}
          >
            Reload
          </Button>
        </div>
      )}

      {/* ENG-R1-002: §7 copy only -- never a raw `error.message`.
          `rejected` (a definitive HTTP error) and `unconfirmed`
          (network/timeout -- an UNKNOWN outcome) get different copy and
          different recovery affordances. */}
      {canCorrect && correction.state.kind === "rejected" && (
        <div
          className="mt-2.5 rounded-lg border border-degraded-border bg-degraded-surface p-2.5 text-sm"
          role="alert"
          data-testid="change-category-error"
        >
          <p>Couldn’t save this category.</p>
          <div className="mt-1.5 flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => correction.state.kind === "rejected" && correction.state.retry()}
            >
              Try again
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => correction.dismiss()}>
              Dismiss
            </Button>
          </div>
        </div>
      )}
      {canCorrect && correction.state.kind === "unconfirmed" && (
        <div
          className="mt-2.5 rounded-lg border border-degraded-border bg-degraded-surface p-2.5 text-sm"
          role="alert"
          data-testid="change-category-unconfirmed"
        >
          <p>We couldn’t confirm the change. Refresh to check its category.</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1.5"
            onClick={handleReload}
          >
            Refresh
          </Button>
        </div>
      )}
      {canCorrect && correction.state.kind === "created_apply_failed" && (
        <div
          className="mt-2.5 rounded-lg border border-degraded-border bg-degraded-surface p-2.5 text-sm"
          role="alert"
          data-testid="change-category-created-apply-failed"
        >
          <p>Type added, but this transaction wasn’t updated.</p>
          <div className="mt-1.5 flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() =>
                correction.state.kind === "created_apply_failed" && correction.state.retry()
              }
            >
              Try again
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => correction.dismiss()}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* §14 decision 3: no more bottom-right tray -- Undo and the
          merchant-apply status render inline, next to the changed control. */}
      {canCorrect && correction.state.kind === "saved" &&
        (correction.state.info.undo || correction.state.info.merchantApply.kind !== "idle") && (
          <div
            className="mt-2.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
            data-testid="change-category-saved-status"
          >
            <span>Saved</span>
            {correction.state.info.merchantApply.kind === "applying" && <span>· Applying to other items…</span>}
            {correction.state.info.merchantApply.kind === "applied" && (
              <>
                {correction.state.info.merchantApply.appliedCount > 0 ? (
                  <span data-testid="change-category-merchant-apply-count">
                    · applied to {correction.state.info.merchantApply.appliedCount} other{" "}
                    {merchantDisplay}{" "}
                    item{correction.state.info.merchantApply.appliedCount === 1 ? "" : "s"}
                  </span>
                ) : (
                  <span data-testid="change-category-merchant-apply-count">
                    · no other {merchantDisplay} items to update
                  </span>
                )}
                <span>A merchant rule was saved; future {merchantDisplay} charges will use it.</span>
              </>
            )}
            {correction.state.info.merchantApply.kind === "no_merchant" && (
              <span>· This transaction has no identified merchant, so it can’t be applied to others.</span>
            )}
            {correction.state.info.merchantApply.kind === "failed" && (
              <span role="alert">· Couldn’t save the merchant rule. Try again.</span>
            )}
            {correction.state.info.merchantApply.kind === "unconfirmed" && (
              <span role="alert">· We couldn’t confirm whether the merchant rule was saved. Refresh before trying again.</span>
            )}
            {correction.state.info.undo && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`Undo category change for ${merchantDisplay ?? "this transaction"}`}
                onClick={() => correction.undoLast()}
                disabled={correction.state.info.undoStatus === "pending"}
                data-testid="change-category-undo"
              >
                {correction.state.info.undoStatus === "pending" ? "Restoring previous category…" : "Undo"}
              </Button>
            )}
            {correction.state.info.undoStatus === "failed" && <span role="alert">Couldn’t restore. Try again.</span>}
            {correction.state.info.undoStatus === "unconfirmed" && (
              <span role="alert">We couldn’t confirm the previous category was restored. Refresh to check.</span>
            )}
          </div>
        )}

      <details className="mt-4 border-t border-border pt-1">
        <summary className="cursor-pointer py-2 text-sm font-medium text-foreground">
          More details
        </summary>
        <div className="space-y-3 pb-1">
          <ReceiptSlot />
          <SimilarList merchantName={merchantDisplay} />
          <AmendmentTrail revisions={txn.revisions} currency={txn.currency} />
          <HistoryList
            allocations={publicAllocationDetails(txn.line_items)}
            currency={txn.currency}
            categories={categoriesQuery.data?.categories}
            categoriesLoading={categoriesQuery.isLoading}
          />
        </div>
      </details>
    </div>
  );
}

export default TransactionDetailPanel;
